import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Sun, Moon, Search, Heart, ChevronDown, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Header = () => {
  const { user, logout } = useAuth();
  const { itemCount, openDrawer } = useCart();
  const { wishlistIds } = useWishlist();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Detect scroll for header style change
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isHome = location.pathname === '/';

  const handleNavClick = (e, to) => {
    if (to === '/') {
      // Navigate to home and scroll to top
      if (location.pathname === '/') {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // If not on home, let the Link handle navigation normally
    } else if (to.startsWith('/#')) {
      e.preventDefault();
      const sectionId = to.replace('/#', '');
      
      if (location.pathname !== '/') {
        // Navigate to home first, then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Already on home, just scroll
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shadow-lg shadow-black/5 dark:shadow-black/20' 
          : isHome 
            ? 'bg-transparent' 
            : 'bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl'
      }`}>
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between ${isHome && !scrolled ? 'h-24' : 'h-20'}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="header-logo">
            <img 
              src="/logo-pcs.png" 
              alt="PCS Dutos e Exaustores" 
              className={`w-auto transition-all duration-500 group-hover:scale-105 ${
                isHome && !scrolled ? 'h-32' : 'h-24'
              } ${
                scrolled || !isHome
                  ? 'brightness-0 dark:brightness-100 dark:invert-0' 
                  : 'brightness-100'
              }`}
              style={{
                filter: scrolled || !isHome 
                  ? 'invert(0) brightness(0)' 
                  : 'none'
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {[
              { to: '/', label: 'Início', testId: 'nav-home' },
              { to: '/shop', label: 'Loja', testId: 'nav-shop' },
              { to: '/#about', label: 'Sobre' },
              { to: '/#contact', label: 'Contato' },
            ].map((link) => (
              <Link 
                key={link.to}
                to={link.to.startsWith('/#') ? '/' : link.to}
                onClick={(e) => handleNavClick(e, link.to)}
                className={`relative px-5 py-2.5 rounded-full font-medium transition-all duration-300 group ${
                  scrolled || !isHome
                    ? 'text-slate-700 dark:text-slate-300 hover:text-cyan-600 dark:hover:text-cyan-400' 
                    : 'text-white/90 hover:text-white'
                }`}
                data-testid={link.testId}
              >
                <span className="relative z-10">{link.label}</span>
                <span className={`absolute inset-0 rounded-full transition-all duration-300 scale-0 group-hover:scale-100 ${
                  scrolled || !isHome
                    ? 'bg-cyan-50 dark:bg-cyan-900/20' 
                    : 'bg-white/10'
                }`}></span>
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              className={`hidden md:flex rounded-full w-10 h-10 transition-all duration-300 ${
                scrolled || !isHome
                  ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' 
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>

            {/* Wishlist */}
            {user && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/wishlist')}
                data-testid="wishlist-btn"
                className={`hidden md:flex relative rounded-full w-10 h-10 transition-all duration-300 ${
                  scrolled || !isHome
                    ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <Heart className="h-5 w-5" />
                {wishlistIds?.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center shadow-lg" data-testid="wishlist-count">
                    {wishlistIds.length}
                  </span>
                )}
              </Button>
            )}

            {/* Cart */}
            <Button
              variant="ghost"
              size="icon"
              className={`relative rounded-full w-10 h-10 transition-all duration-300 ${
                scrolled || !isHome
                  ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' 
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              onClick={openDrawer}
              data-testid="cart-btn"
            >
              <ShoppingCart className="h-5 w-5" />
              {user && itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg animate-pulse" data-testid="cart-count">
                  {itemCount}
                </span>
              )}
            </Button>

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    data-testid="user-menu-btn"
                    className={`rounded-full w-10 h-10 transition-all duration-300 ${
                      scrolled || !isHome
                        ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' 
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl">
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{user.name || user.email}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                  </div>
                  <div className="py-2">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard" className="cursor-pointer">
                      Meu Painel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="menu-orders" className="cursor-pointer">
                      Meus Pedidos
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/wishlist')} data-testid="menu-wishlist" className="cursor-pointer">
                      Favoritos
                    </DropdownMenuItem>
                    {(user.role === 'admin' || user.role === 'programmer') && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin" className="cursor-pointer text-cyan-600 dark:text-cyan-400">
                        <Sparkles className="h-4 w-4 mr-2" />
                        Painel Admin
                      </DropdownMenuItem>
                    )}
                  </div>
                  <div className="border-t border-slate-100 dark:border-slate-800 py-2">
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="cursor-pointer text-red-600 dark:text-red-400">
                      Sair
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                onClick={() => navigate('/auth')} 
                data-testid="login-btn"
                className={`rounded-full px-6 font-medium transition-all duration-300 ${
                  scrolled || !isHome
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40' 
                    : 'bg-white text-slate-900 hover:bg-white/90 shadow-lg'
                }`}
              >
                Entrar
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className={`md:hidden rounded-full w-10 h-10 ${
                scrolled || !isHome
                  ? 'text-slate-600 dark:text-slate-300' 
                  : 'text-white'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-6 border-t border-slate-200/50 dark:border-slate-700/50 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl rounded-b-2xl">
            <nav className="flex flex-col space-y-1 px-2">
              {[
                { to: '/', label: 'Início' },
                { to: '/shop', label: 'Loja' },
                { to: '/#about', label: 'Sobre' },
                { to: '/#contact', label: 'Contato' },
              ].map((link) => (
                <Link 
                  key={link.to}
                  to={link.to.startsWith('/#') ? '/' : link.to}
                  className="px-4 py-3 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl font-medium transition-colors" 
                  onClick={(e) => {
                    handleNavClick(e, link.to);
                    setMobileMenuOpen(false);
                  }}
                >
                  {link.label}
                </Link>
              ))}
              <button 
                onClick={() => { toggleTheme(); setMobileMenuOpen(false); }} 
                className="px-4 py-3 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl font-medium transition-colors text-left flex items-center"
              >
                {theme === 'light' ? <Moon className="h-5 w-5 mr-3" /> : <Sun className="h-5 w-5 mr-3" />}
                {theme === 'light' ? 'Modo Escuro' : 'Modo Claro'}
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
    </>
  );
};